import {
  combineLatest,
  Observable,
  Subscription,
  throwError as observableThrowError,
  throwError,
} from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Inject, inject, Injectable, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { each } from 'lodash';
import { select, Store } from '@ngrx/store';
import { Loader } from '@googlemaps/js-api-loader';
import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import { environment } from '../../../environments/environment';
import { CompanyAddress, Farm, Field } from '../_models';
import { AppState } from '../_reducers';
import { selectCurrentFarm } from '../_selectors/currentFarm.selectors';
import { selectAllFields } from '../_selectors/field.selectors';
import { selectAllCompanyAddresses } from '../_selectors/companyAddress.selectors';
import { GeneralUtilsService } from './common/generalUtils.service';

@Injectable()
export class MapService implements OnDestroy {
  // Services
  private generalUtilsService = inject(GeneralUtilsService);
  private httpClient = inject(HttpClient);
  private http = inject(HttpClient);
  private store = inject(Store<AppState>);

  private apiBaseUrl = environment.apiBaseUrl;
  private defaultLat = 42.48830197960228;
  private defaultLng = 12.81005859375;
  private defaultZoom = 10;
  private usrLocation: LocationObj = null;
  private subscriptions: Subscription = new Subscription();
  private bounds: google.maps.LatLngBounds;
  private googleMapsApiLoader: Loader;
  public customBounds$: BehaviorSubject<google.maps.LatLngBounds> =
    new BehaviorSubject<google.maps.LatLngBounds>(null);

  constructor(
    @Inject('googleMapsApiKey') private apiKey: string,
    @Inject('googleMapsLibraries') private libraries: any
  ) {
    this.googleMapsApiLoader = new Loader({
      apiKey: this.apiKey,
      libraries: this.libraries,
    });

    this.googleMapsApiLoader.load().then(() => {
      this.bounds = new google.maps.LatLngBounds();
    });
  }

  getmapShapes(): Observable<any[]> {
    return this.http.get<any[]>(this.apiBaseUrl + 'api/private/v1/mapShape');
  }

  savemapShape(mapShapeItem): Observable<any> {
    const mapShape = { ...mapShapeItem };
    delete mapShape.lastUpdated;
    delete mapShape.dateCreated;

    const companySelected = JSON.parse(localStorage.getItem('companySelected'));
    mapShape.companyId = companySelected ? companySelected.id : null;

    if (mapShape.id) {
      return this.http
        .put<any>(this.apiBaseUrl + 'api/private/v1/mapShape/' + mapShape.id, mapShape)
        .pipe(map((response) => response));
    } else {
      return this.http
        .post<any>(this.apiBaseUrl + 'api/private/v1/mapShape', mapShape)
        .pipe(map((response) => response));
    }
  }

  getFarmMapData = () => {
    return new Observable<LocationObj>((observer) => {
      const farm$: Observable<Farm> = this.store.select(selectCurrentFarm);
      const addressList$: Observable<CompanyAddress[]> = this.store.pipe(
        select(selectAllCompanyAddresses)
      );
      const fieldList$: Observable<Field[]> = this.store.pipe(select(selectAllFields));

      const subs = combineLatest([farm$, addressList$, fieldList$]).subscribe(
        ([currentFarm, addresses, fields]) => {
          if (currentFarm && currentFarm.id != null && currentFarm.lat && currentFarm.lng) {
            const result = {
              lat: currentFarm.lat,
              lng: currentFarm.lng,
              zoom: 13,
            } as LocationObj;
            observer.next(result);
            observer.complete();
          } else if (this.usrLocation) {
            observer.next(this.usrLocation);
            observer.complete();
          } else {
            const filteredAddresses = addresses.filter(
              (address: CompanyAddress) => address.type == 'operative' && address.lat && address.lng
            );
            if (filteredAddresses && filteredAddresses.length > 0) {
              const firstAddress = filteredAddresses[0];
              this.usrLocation = {
                lat: firstAddress.lat,
                lng: firstAddress.lng,
                zoom: this.defaultZoom,
              } as LocationObj;
              observer.next(this.usrLocation);
              observer.complete();
            } else if (
              fields &&
              fields.length > 0 &&
              (!filteredAddresses || filteredAddresses.length === 0)
            ) {
              fields.forEach((field: Field) => {
                if (
                  field &&
                  field.polygon &&
                  field.polygon.coordinates &&
                  field.polygon.coordinates.length > 0 &&
                  this.generalUtilsService.getValidateJsonString(field.geoJsonArea)
                ) {
                  each(JSON.parse(field.geoJsonArea).features, (feature: any) => {
                    const polygonCoords = [];
                    if (feature.geometry !== undefined) {
                      const tmpCoords = feature.geometry.coordinates;
                      each(tmpCoords, (poly) => {
                        each(poly, (point) => {
                          const gmapPoint = { lat: +point[1], lng: +point[0] };
                          polygonCoords.push(gmapPoint);
                          this.bounds.extend(gmapPoint);
                        });
                      });
                    }
                  });
                }
              });

              if (this.bounds.getCenter().lat() && this.bounds.getCenter().lng()) {
                this.usrLocation = {
                  lat: this.bounds.getCenter().lat(),
                  lng: this.bounds.getCenter().lng(),
                  zoom: this.defaultZoom,
                } as LocationObj;
                observer.next(this.usrLocation);
                observer.complete();
              }
            } else if (
              (!fields || fields.length === 0) &&
              (!filteredAddresses || filteredAddresses.length === 0)
            ) {
              this.generalUtilsService.getUserIPLookUp().subscribe(
                (response: any) => {
                  if (response && response.latitude && response.longitude) {
                    this.usrLocation = {
                      lat: response.latitude,
                      lng: response.longitude,
                      zoom: this.defaultZoom,
                    };
                  } else if (
                    response &&
                    response.location &&
                    response.location.lat &&
                    response.location.lng
                  ) {
                    this.usrLocation = {
                      lat: response.location.lat,
                      lng: response.location.lng,
                      zoom: this.defaultZoom,
                    };
                  } else {
                    this.usrLocation = this.getStaticLocation();
                  }
                },
                () => {
                  this.usrLocation = this.getStaticLocation();
                },
                () => {
                  observer.next(this.usrLocation);
                  observer.complete();
                }
              );
            }
          }
        }
      );
      this.subscriptions.add(subs);
    });
  };

  private getStaticLocation(): LocationObj {
    return {
      lat: this.defaultLat,
      lng: this.defaultLng,
      zoom: this.defaultZoom,
    };
  }

  getGeoJsonFeatureCollection() {
    return {
      type: 'FeatureCollection',
      features: [],
    };
  }

  getGeoJsonFeature(type: string = 'Polygon') {
    return {
      type: 'Feature',
      properties: {},
      geometry: {
        type,
        coordinates: [],
      },
    };
  }

  createStaticMapCircle(
    lat: number,
    lng: number,
    radius: number,
    detail: number = 1,
    areaColor: string
  ) {
    const urlApiMaps = `${this.apiBaseUrl}api/private/maps/staticmap`;
    const size = 'size=250x250';
    const mapType = 'maptype=hybrid';
    const color = 'path=color:0x' + `${areaColor}` + '|weight:3|fillcolor:0x' + `${areaColor}`;

    let staticMapSrc = 'center=' + lat + ',' + lng;
    staticMapSrc += '&size=100x100';
    staticMapSrc += '&' + mapType;
    staticMapSrc += '&' + color;

    const r = 6371;
    const pi = Math.PI;
    const _lat = (lat * pi) / 180;
    const _lng = (lng * pi) / 180;
    const d = radius / r;

    for (let i = 0; i <= 360; i += detail) {
      const brng = (i * pi) / 180;
      let pLat = Math.asin(
        Math.sin(_lat) * Math.cos(d) + Math.cos(_lat) * Math.sin(d) * Math.cos(brng)
      );
      const pLng =
        ((_lng +
          Math.atan2(
            Math.sin(brng) * Math.sin(d) * Math.cos(_lat),
            Math.cos(d) - Math.sin(_lat) * Math.sin(pLat)
          )) *
          180) /
        pi;
      pLat = (pLat * 180) / pi;
      staticMapSrc += '|' + pLat + ',' + pLng;
    }

    return urlApiMaps + '?' + '&' + size + '&' + encodeURI(staticMapSrc);
  }

  public getCadastralPolygons(coordX, coordY, zoom): Observable<any> {
    return this.http
      .get<any>(
        this.apiBaseUrl +
          'api/private/v3/landRegistry/es/cadastralPolygons?zoom=' +
          zoom +
          '&coordX=' +
          coordX +
          '&coordY=' +
          coordY
      )
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          return observableThrowError(error);
        })
      );
  }

  public getCadastralRecintoBoxByParcel(
    provincia,
    municipio,
    poligono,
    parcela,
    agregado,
    zona,
    recinto
  ): Observable<any> {
    return this.http
      .get<any>(
        this.apiBaseUrl +
          'api/private/v3/landRegistry/es/cadastralRecintoBoxByParcel?provincia=' +
          provincia +
          '&municipio=' +
          municipio +
          '&poligono=' +
          poligono +
          '&parcela=' +
          parcela +
          '&agregado=' +
          agregado +
          '&zona=' +
          zona +
          '&recinto=' +
          recinto
      )
      .pipe(
        map((response) => response),
        catchError((error: HttpErrorResponse) => {
          return observableThrowError(error);
        })
      );
  }

  public searchCarParcelsByText(car: string): Observable<any> {
    const url =
      this.apiBaseUrl +
      'api/private/v3/landRegistry/bra/consultaPolygons?text=' +
      encodeURIComponent(car);

    return this.http.get<any>(url).pipe(
      map((response) => response),
      catchError((error: HttpErrorResponse) => {
        return throwError(() => new Error(error.message || 'Server Error'));
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    this.customBounds$.next(null);
  }

  public findLatLngEqual(markerList: any[]): any[] {
    const seenCoords = new Set<string>();
    markerList.forEach((marker, index) => {
      const coordString = `${marker.lat},${marker.lng}`;
      if (seenCoords.has(coordString)) {
        const newLatLng = this.addRandomOffsetToLatLng(
          { lat: marker.lat, lng: marker.lng },
          0.0001
        );
        markerList[index].lat = newLatLng.lat;
        markerList[index].lng = newLatLng.lng;
      } else {
        seenCoords.add(coordString);
      }
    });

    return markerList;
  }

  addRandomOffsetToLatLng(
    latlng: { lat: any; lng: any },
    maxOffset: number
  ): { lat: any; lng: any } {
    const latOffset = (Math.random() * 2 - 1) * maxOffset;
    const lngOffset = (Math.random() * 2 - 1) * maxOffset;
    return {
      lat: latlng.lat + latOffset,
      lng: latlng.lng + lngOffset,
    };
  }

  public getCadastralPolygonsPacBra(bbox: string, stateAcronym: string): Observable<any> {
    const url = `${this.apiBaseUrl}api/private/v3/landRegistry/bra/cadastralPolygons?bbox=${bbox}&layer=sicar%3Asicar_imoveis_${stateAcronym}`;

    return this.http.get<any>(url).pipe(
      map((response) => response), // `HttpClient` gestisce automaticamente il parsing JSON, quindi restituiamo la risposta direttamente
      catchError((error: HttpErrorResponse) => {
        return throwError(() => new Error(error.message || 'Server Error')); // `throwError` per gestire gli errori
      })
    );
  }
}

export interface LocationObj {
  lat: number;
  lng: number;
  zoom: number;
}

export interface LocationAddress {
  city: string;
  country: string;
  countryCode: string;
}

export interface CircleItem {
  lat: number;
  lng: number;
  radius: number;
  options: CircleOptions;
  marker?: google.maps.MarkerOptions | any;
  data?: any;
}

export class CircleOptions {
  strokeColor: string = 'red';
  strokeWeight: number = 3;
  strokeOpacity: number = 1;
  fillColor: string = 'red';
  fillOpacity: number = 0.3;
  visible: boolean = true;
  editable: boolean = false;
  draggable: boolean = false;
  strokePosition: keyof typeof google.maps.StrokePosition = 'OUTSIDE';
}
